import { type ReactNode } from 'react';

import {
  ButtonIcon,
  Chip,
  EncoreProvider,
  Flex,
  IconX,
  Pressable,
  ProgressCircle,
  type ProgressCircleProps,
} from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { useDelayedLoading } from '../../hooks/useDelayedLoading';
import { FilterViewer } from './FilterViewer';
import { type FilterViewerComponentProps } from './provider';

import styles from './FilterChip.module.css';

const FilterLoadingIndicator = ({
  size,
}: Pick<ProgressCircleProps, 'size'>) => (
  <span aria-hidden>
    <ProgressCircle
      aria-label="Loading filter"
      className={styles.loadingIndicator}
      size={size}
    />
  </span>
);

export type FilterChipProps<T> = FilterViewerComponentProps<T> & {
  label: ReactNode;
  loading?: boolean;
  tooltip?: ReactNode;
};

export const FilterChip = <T,>({
  label,
  loading,
  tooltip,
  ...rest
}: FilterChipProps<T>) => {
  const { showLoading } = useDelayedLoading(Boolean(loading));
  const removeFilterLabel = `Remove ${rest.filter.label} filter`;
  const loadingFilterLabel = `Loading ${rest.filter.label} filter`;

  return (
    <EncoreProvider config={{ next: ['chip'] }}>
      <Flex inline alignItems="center" className={styles.editableChip}>
        <FilterViewer {...rest}>
          {({ isOpen, onOpenChange, triggerRef }) => {
            const chip = (
              <Chip
                ref={triggerRef}
                className={styles.chip}
                disabled={loading}
                iconLeading={FilterLoadingIndicator}
                iconLeadingIsHidden={!showLoading}
                variant="bordered"
                type="button"
                aria-busy={showLoading || undefined}
                aria-label={showLoading ? loadingFilterLabel : undefined}
                data-filter-id={rest.filter.id}
                onClick={() => onOpenChange(!isOpen)}
              >
                {showLoading ? rest.filter.label : label}
              </Chip>
            );

            return tooltip ? (
              <TooltipRoot>
                <Pressable>{chip}</Pressable>
                <Tooltip>{tooltip}</Tooltip>
              </TooltipRoot>
            ) : (
              chip
            );
          }}
        </FilterViewer>
        {!loading && (
          <TooltipRoot>
            <ButtonIcon
              className={styles.removeButton}
              variant="tertiary"
              size="small"
              hasNoInteractiveBackground
              aria-label={removeFilterLabel}
              onClick={rest.onRemove}
            >
              <IconX aria-hidden />
            </ButtonIcon>
            <Tooltip>{removeFilterLabel}</Tooltip>
          </TooltipRoot>
        )}
      </Flex>
    </EncoreProvider>
  );
};
